import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { api } from '../../lib/api';
import LoadingSpinner from '../shared/LoadingSpinner';
import EmptyState from '../shared/EmptyState';
import InviteAdminModal from './InviteAdminModal';

const TYPE_COLORS = { law_firm: '#F59E0B', marketer: '#8B5CF6', medical_practice: '#10B981' };

// What kind of user this is, derived from memberships: a `client` role
// anywhere makes them a Patient (they can belong to a law firm AND a
// provider); otherwise they're staff of whichever org type they belong to.
function userKind(u) {
  const ms = u.memberships || [];
  if (ms.some(m => m.role === 'client')) return { label: 'Patient', color: '#8b5cf6' };
  if (ms.some(m => m.org?.type === 'medical_practice')) return { label: 'Provider', color: TYPE_COLORS.medical_practice };
  if (ms.some(m => m.org?.type === 'law_firm')) return { label: 'Law firm', color: TYPE_COLORS.law_firm };
    if (ms.some(m => m.org?.type === 'marketer')) return { label: 'Marketer', color: TYPE_COLORS.marketer };
  if (u.is_platform_admin) return { label: 'Admin', color: '#0DA7CA' };
  return null;
}

export default function UserList() {
  const [search, setSearch] = useState('');
  const [showInviteAdmin, setShowInviteAdmin] = useState(false);
  const [stalledOnly, setStalledOnly] = useState(false);

  const [users, setUsers] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.adminListUsers()
      .then(setUsers)
      .catch(() => setUsers([]))
      .finally(() => setLoading(false));
  }, []);

  const stalledCount = users?.filter(u => u.signup_stage).length || 0;

  const filtered = users?.filter(u => {
    if (stalledOnly && !u.signup_stage) return false;
    const term = search.toLowerCase();
    return (u.first_name || '').toLowerCase().includes(term)
      || (u.last_name || '').toLowerCase().includes(term)
      || (u.email || '').toLowerCase().includes(term)
      || (u.memberships || []).some(m => (m.org?.name || '').toLowerCase().includes(term));
  }) || [];

  const inputClass = 'w-full px-3.5 py-[11px] bg-tertiary border border-strong rounded-[10px] text-primary-c text-[13px] font-[inherit] transition-all focus:outline-none focus:border-[#0DA7CA] focus:shadow-[0_0_0_3px_rgba(13, 167, 202,0.15)]';

  return (
    <div className="flex-1 overflow-y-auto bg-primary">
      <div className="max-w-[1200px] mx-auto px-4 md:px-7 py-6">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-[22px] font-bold text-primary-c">Users</h1>
          <button
            onClick={() => setShowInviteAdmin(true)}
            className="flex items-center gap-2 px-4 py-2.5 gradient-accent text-white rounded-[10px] text-[13px] font-semibold shadow-glow hover:-translate-y-0.5 transition-all"
          >
            <span className="text-lg leading-none">+</span> Invite Admin
          </button>
        </div>

        <div className="mb-4 flex items-center gap-2">
          <input
            type="text"
            className={inputClass}
            placeholder="Search users..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {stalledCount > 0 && (
            <button
              onClick={() => setStalledOnly(v => !v)}
              className={`shrink-0 px-3.5 py-[11px] rounded-[10px] text-[12px] font-semibold border transition-all whitespace-nowrap ${stalledOnly
                ? 'gradient-accent text-white border-transparent shadow-glow'
                : 'bg-tertiary text-secondary-c border-theme hover:bg-hover-surface'}`}
            >
              Stalled signups ({stalledCount})
            </button>
          )}
        </div>

        {loading ? (
          <LoadingSpinner size="lg" className="py-16" />
        ) : filtered.length === 0 ? (
          <EmptyState title={search ? 'No results' : 'No users'} description="No users found." />
        ) : (
          <div className="bg-elevated rounded-xl border border-theme overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-theme">
                    <th className="text-left text-[11px] font-semibold text-muted-c uppercase tracking-wider px-5 py-3">Name</th>
                    <th className="text-left text-[11px] font-semibold text-muted-c uppercase tracking-wider px-5 py-3">Type</th>
                    <th className="text-left text-[11px] font-semibold text-muted-c uppercase tracking-wider px-5 py-3">Organizations</th>
                    <th className="text-left text-[11px] font-semibold text-muted-c uppercase tracking-wider px-5 py-3 hidden md:table-cell">Email</th>
                    <th className="text-left text-[11px] font-semibold text-muted-c uppercase tracking-wider px-5 py-3 hidden lg:table-cell">Last sign-in</th>
                    <th className="text-left text-[11px] font-semibold text-muted-c uppercase tracking-wider px-5 py-3 hidden xl:table-cell">Joined</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--border)]">
                  {filtered.map(user => {
                    const kind = userKind(user);
                    const orgs = (user.memberships || []).filter(m => m.org);
                    return (
                      <tr key={user.id} className="hover:bg-hover-surface transition-all">
                        <td className="px-5 py-3.5">
                          <Link to={`/admin/users/${user.id}`} className="text-[13px] font-semibold text-accent-light hover:text-accent transition-colors">
                            {user.first_name} {user.last_name}
                          </Link>
                        </td>
                        <td className="px-5 py-3.5">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            {kind && (
                              <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full"
                                    style={{ color: kind.color, background: `${kind.color}1a` }}>
                                {kind.label}
                              </span>
                            )}
                            {kind?.label !== 'Admin' && user.is_platform_admin && (
                              <span className="text-[11px] font-semibold text-accent-light bg-accent-glow px-2.5 py-1 rounded-full">Admin</span>
                            )}
                          </div>
                        </td>
                        <td className="px-5 py-3.5">
                          {orgs.length ? (
                            <div className="flex flex-wrap gap-1.5 max-w-[320px]">
                              {orgs.map((m, i) => (
                                <span key={i} className="inline-flex items-center gap-1.5 text-[11px] text-secondary-c bg-tertiary px-2 py-0.5 rounded-full whitespace-nowrap">
                                  <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: TYPE_COLORS[m.org.type] || '#6B7280' }} />
                                  {m.org.name}
                                </span>
                              ))}
                            </div>
                          ) : user.signup_stage ? (
                            <div>
                              <span className={`inline-block text-[11px] font-semibold px-2.5 py-1 rounded-full ${
                                user.signup_stage.startsWith('Blocked') ? 'bg-danger-soft text-danger'
                                : user.signup_stage.startsWith('Reached payment') || user.signup_stage.startsWith('ID verified') ? 'bg-warning-soft text-warning'
                                : 'bg-tertiary text-muted-c'}`}>
                                {user.signup_stage}
                              </span>
                              {user.nudged && (
                                <div className="text-[10px] text-muted-c mt-1">Nudge email sent</div>
                              )}
                            </div>
                          ) : (
                            <span className="text-[12px] text-muted-c">—</span>
                          )}
                        </td>
                        <td className="px-5 py-3.5 hidden md:table-cell text-[13px] text-secondary-c">{user.email}</td>
                        <td className="px-5 py-3.5 hidden lg:table-cell text-[12px] text-muted-c">
                          {user.last_sign_in_at
                            ? formatDistanceToNow(new Date(user.last_sign_in_at), { addSuffix: true })
                            : <span className="opacity-60">never</span>}
                        </td>
                        <td className="px-5 py-3.5 hidden xl:table-cell text-[12px] text-muted-c">
                          {new Date(user.created_at).toLocaleDateString()}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {showInviteAdmin && (
        <InviteAdminModal
          onClose={() => setShowInviteAdmin(false)}
          onSent={() => window.location.reload()}
        />
      )}
    </div>
  );
}
